import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import prettyExpression from 'ui-library/charting/statistics/regression/prettyExpression';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingPlacement, FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import RegressionOutputTooltipContent from '../RegressionOutputTooltipContent';
import {DEFAULT_CHART_MARGIN} from '../utils/Theme';
import useVisXContext, {VisXContext} from './hooks/useVisXContext';

export interface RegressionSummaryProps {
  regressionAnalysis: SuccessfulAnalysis<RegressionAnalysis>;
}

function _PresentationalRegressionSummary(
  props: RegressionSummaryProps & Pick<VisXContext, 'width' | 'margin'>
): JSX.Element {
  const sharedSectionProps = {
    ...getTestProps({component: 'RegressionSummary', element: 'summarySection'}),
    className: css(regressionSummaryStyles.section),
  };

  let sections = props.regressionAnalysis.result.outputValues
    .filter(({showInPlotHeader}) => showInPlotHeader)
    .map(({fieldName, fieldNameDisplayOverride, value}) => (
      <span {...sharedSectionProps} key={fieldName}>
        {`${fieldNameDisplayOverride ?? fieldName} = ${prettyExpression(value, null)}`}
      </span>
    ));

  if (props.regressionAnalysis.result.fitEquation) {
    sections = [
      <span {...sharedSectionProps} key="equation">
        {props.regressionAnalysis.result.fitEquation}
      </span>,
      ...sections,
    ];
  }

  return (
    <foreignObject width={props.width} height={DEFAULT_CHART_MARGIN + 2} x={0} y={0}>
      <div style={{marginRight: props.margin.right}} className={css(regressionSummaryStyles.wrapper)}>
        {sections}
        <Popover
          renderContent={() => (
            <RegressionOutputTooltipContent regressionResult={props.regressionAnalysis.result} />
          )}
          placement={FloatingPlacement.LEFT}
          interaction={FloatingInteraction.CLICK}
        >
          <span className={css(regressionSummaryStyles.infoIcon)}>
            <BenchlingIcon icon="info-circle" />
          </span>
        </Popover>
      </div>
    </foreignObject>
  );
}

// Memoize the presentational component so it doesn't get rerendered unnecessarily, for example when
// hovering over a scatter plot point which results in a rerender of useVisXContext.
const PresentationalRegressionSummary = React.memo(_PresentationalRegressionSummary);

export default function RegressionSummary(props: RegressionSummaryProps): JSX.Element {
  return <PresentationalRegressionSummary {...props} {...useVisXContext('width', 'margin')} />;
}

export const regressionSummaryStyles = StyleSheet.create({
  wrapper: {
    overflowX: 'hidden',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  section: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginLeft: 24,
  },
  infoIcon: {
    cursor: 'pointer',
    color: GRAY_DARKER,
    marginLeft: 24,
  },
});
